'use client';
import React, { useState } from 'react';
import HowItWorksItem from './HowItWorksItem';
import { useTranslations } from 'next-intl';
import { Modal, Skeleton, Spin } from 'antd';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { LoadingOutlined } from '@ant-design/icons';
import AfiwaButton from '@/ui/shared/components/afiwaButton';
import appRoutes from '@/constants/routes';
import SectionHeading from '@/ui/shared/components/sectionHeading';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';

type HowItWorksItemProps = {
  title: string;
  description: string;
  onClick: (indexItem: number) => void;
  imgCoverPath: string;
  href: string;
};

function HowItWorks() {
  const howItWorksText = useTranslations('tutorials.how_it_works');
  const homepageText = useTranslations('homepage');
  const [visible, setVisible] = useState(false);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [loadingFrame, setLoadingFrame] = useState(true);

  const handleIframeLoad = () => {
    setLoadingFrame(false);
  };

  // Function to show the drawer
  const showDrawer = (index: number) => {
    setClickedIndex(index);
    setVisible(true);
  };

  // Function to close the drawer
  const closeDrawer = () => {
    setVisible(false);
    setClickedIndex(null); // Reset clicked index when drawer is closed
  };

  const howItWorksList: HowItWorksItemProps[] = [
    {
      title: howItWorksText('step1_title'),
      description: howItWorksText('step1_description'),
      onClick: showDrawer,
      imgCoverPath: '/tutorials/tutorial1.png',
      href: 'https://www.youtube.com/embed/czVH-0Nu-KY',
    },
    {
      title: howItWorksText('step2_title'),
      description: howItWorksText('step2_description'),
      onClick: showDrawer,
      imgCoverPath: '/tutorials/tutorial2.png',
      href: 'https://www.youtube.com/embed/1s1H6lUexG0',
    },
    {
      title: howItWorksText('step3_title'),
      description: howItWorksText('step3_description'),
      onClick: showDrawer,
      imgCoverPath: '/tutorials/tutorial3.png',
      href: 'https://www.youtube.com/embed/rFsqM-4mJwM',
    },
    {
      title: howItWorksText('step4_title'),
      description: howItWorksText('step4_description'),
      onClick: showDrawer,
      imgCoverPath: '/tutorials/tutorial5.png',
      href: 'https://www.youtube.com/embed/rLkcKtYuLHo?si=KdZHnRrEi4Bc135V',
    },
    {
      title: howItWorksText('step5_title'),
      description: howItWorksText('step5_description'),
      onClick: showDrawer,
      imgCoverPath: '/tutorials/tutorial6.jpeg',
      href: 'https://www.youtube.com/embed/ui85_CRXbMY?si=Uzn6dLoRYlz-M9fE',
    },
  ];

  return (
    <div className="my-20 md:my-32">
      <SectionHeading className="mx-auto my-10 space-x-2 text-center font-extrabold  md:my-16">
        <span className="text-primary-dark">
          {howItWorksText('how_it_works')}
        </span>
        <span className="text-primary">{howItWorksText('easy_steps')}</span>
      </SectionHeading>

      <div className="rounded-md bg-white p-10 md:p-16">
        <div className="flex flex-col gap-y-10 md:gap-y-16">
          <p className="flex flex-col items-start justify-start">
            <span className="text-xs font-light text-primary-pale md:text-sm">
              {howItWorksText('tutorial_to_make_transaction')}
            </span>
            <span className="text-2xl font-extrabold text-primary-dark md:text-3xl">
              {howItWorksText('tutorials')}
            </span>
          </p>
          <div className="flex flex-col  justify-start gap-y-32 md:gap-y-16">
            {howItWorksList.map((item, index) => (
              <HowItWorksItem
                key={index}
                title={item.title}
                description={item.description}
                onClick={() => item.onClick(index)}
                imgCoverPath={item.imgCoverPath}
                href={item.href}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        <AfiwaButton
          type="primary"
          size="large"
          wrapperClass="mt-20"
          className="!md:text-base  !bg-primary !text-xs !font-semibold !text-white"
          href={appRoutes.login}
          text={homepageText('start_seamless_journey')}
          rightIcon={<ArrowRightIcon height={15} width={15} />}
        />
      </div>

      {clickedIndex !== null && (
        <>
          <Modal
            title={null}
            footer={null}
            centered
            open={visible}
            onCancel={closeDrawer}
            okButtonProps={{ disabled: true }}
            cancelButtonProps={{ disabled: true }}
            className="custom-modal w-[100%] lg:min-w-[853px] "
            closeIcon={
              <XMarkIcon
                className={`absolute ${
                  loadingFrame ? '-right-5 -top-64' : 'right-1 top-1'
                }  z-20  h-8 w-8 text-white`}
              />
            }
          >
            {loadingFrame && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 120, color: '#5C7DAD' }}
                      spin
                    />
                  }
                />
              </div>
            )}

            <div
              style={{ position: 'relative', height: '100%', width: '100%' }}
            >
              <iframe
                src={howItWorksList[clickedIndex].href}
                title={howItWorksList[clickedIndex].title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '500px' }}
                className="rounded-2xl"
                onLoad={handleIframeLoad}
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}

export default HowItWorks;
